import { useEffect, useRef, useState } from "react";
import { Box, TextField } from "@mui/material";
import { useShallow } from "zustand/react/shallow";

import styles from "../../../../../assets/styles/componentStyles/templateLibraryStyles/whatsappTemplatePreview.module.scss";

import VariableDialog from "../../utils/VariableDialog";
import { renderVariableInText } from "../../utils/renderVariableInText";
import { useTemplateLibraryStore } from "../../../../../store/templateLibraryStore";
import { useCarouselFunctions } from "../../utils/useCarouselFunctions";
import { handleKeyDownForFormatting, isTemplateCreated } from "../../constant";

const CarouselTemplateCardBodyPreview = (props: any) => {
    const { 
        cardData, cardIndex, editable = false, handleChangeText 
    } = props;

    const { createTemplatePayload } = useTemplateLibraryStore(useShallow((state) => ({
        createTemplatePayload: state.createTemplatePayload
    })));

    const { textareaRefs, handleFormattingCarousel, handleChangeCarouselVar, handleRemoveCarouselField, checkFlagErrorCarousel, 
        handleCarouselCardBodyChange } = useCarouselFunctions();

    const bodyTextareaRef = useRef<HTMLTextAreaElement | null>(null);

    const [editingText, setEditingText] = useState(false);
    const [variableDialogOpen, setVariableDialogOpen] = useState(false);
    const [variableDialogAnchorEl, setVariableDialogAnchorEl] = useState<HTMLElement | null>(null);
    const [activeVariableKey, setActiveVariableKey] = useState<string | null>(null);

    // Constants
    const bodyVariableData = cardData?.whatsapp_template_body?.whatsapp_template_body_variable_data ?? [];
    const bodyTextData = cardData?.whatsapp_template_body?.whatsapp_template_body_value ?? "";
    const isEmptyText = !bodyTextData?.trim();
    const hasMissingSampleText = bodyVariableData?.some(
        (variable: any) => !variable?.whatsapp_template_dynamic_variable_sample_value || variable?.whatsapp_template_dynamic_variable_sample_value?.trim() === ""
    );

    const isDisabled = isTemplateCreated(createTemplatePayload);
    const hasTooManyVariables = checkFlagErrorCarousel("body", cardIndex);

    const bodyTextClassName = [
        styles.whatsappPreview__bodyText,
        editable && (hasMissingSampleText || isEmptyText || hasTooManyVariables) ? styles.whatsappPreview__textInputError : "",
    ].filter(Boolean).join(" ");


    // Dynamic Variable Functions
    const handleVariableAreaClick = (e: React.MouseEvent) => {
        const target = e.target as HTMLElement;
        const variableElement = target.closest?.('[data-variable="true"]') || target.closest?.(`.${styles.whatsappPreview__variable}`);

        if (variableElement) {
            e.preventDefault();
            e.stopPropagation();
            setVariableDialogAnchorEl(variableElement as HTMLElement);
            setActiveVariableKey((variableElement as HTMLElement).getAttribute("data-variable-key"));
            setVariableDialogOpen(true);
        } else if (editable && !isDisabled) {
            setEditingText(true);
        }
    };

    const handleVariableDialogClose = () => {
        setVariableDialogOpen(false);
        setVariableDialogAnchorEl(null);
        setActiveVariableKey(null);
    };


    // UseEffect
    useEffect(() => {
        if (!editable) {
            setVariableDialogOpen(false);
            setVariableDialogAnchorEl(null);
            setEditingText(false);
        }
    }, [editable]);

    useEffect(() => {
        if (editingText && bodyTextareaRef?.current) {
            const textTimeout = setTimeout(() => {
                const textareaElement = bodyTextareaRef.current;
                if (!textareaElement) return;
                textareaElement.focus();
                const bodyValueLength = bodyTextData?.length;
                textareaElement.setSelectionRange(bodyValueLength, bodyValueLength);
            }, 0);
            return () => clearTimeout(textTimeout);
        }
    }, [editingText]);


    // Body Preview
    const renderBodyPreview = () => (
        <Box 
            className={`${styles.whatsappPreview__bodyText} ${editable && (hasMissingSampleText || isEmptyText) ? styles.whatsappPreview__textInputError : ""}`}
            dir="auto"
        >
          {bodyTextData?.trim()
            ? renderVariableInText(bodyTextData, bodyVariableData)
            : (
                <span
                  className={styles.whatsappPreview__textContentPreview}
                  dangerouslySetInnerHTML={{ __html: "Enter your body text here" }}
                />
              )}
        </Box>
    );

    if (!editable) {
        return renderBodyPreview();
    }

    return (
        <>
          {editable ? (
                <div 
                    onClick={handleVariableAreaClick}
                    className={styles.whatsappPreview__editableTextFieldWrapper}
                    onMouseLeave={() => setEditingText(false)}
                >
                    {editingText ? (
                        <TextField
                            multiline
                            fullWidth
                            inputProps={{ dir: 'auto' }}
                            InputProps={{ className: styles.whatsappPreview__textInputHoverContainer }}
                            disabled={isDisabled}
                            inputRef={(el) => {
                                bodyTextareaRef.current = el;
                                textareaRefs.current[cardIndex] = el;
                            }}
                            className={bodyTextClassName}
                            value={bodyTextData}
                            onChange={(event: any) => {handleCarouselCardBodyChange(event, cardIndex, handleChangeText)}}
                            onKeyDown={(e) => handleKeyDownForFormatting(e, handleFormattingCarousel, "body", cardIndex)}
                            autoComplete="off"
                            minRows={2}
                            placeholder="Enter body text"
                        />
                    ) : (
                        renderBodyPreview()
                    )}
                </div>
            ) : (
              renderBodyPreview()
            )
          }
  
          {editable && (
            <VariableDialog
                open={variableDialogOpen}
                anchorEl={variableDialogAnchorEl}
                onClose={handleVariableDialogClose}
                title="Variable"
                variableData={bodyVariableData}
                variableType="body"
                activeVariableKey={activeVariableKey}
                onChange={(key, event) => handleChangeCarouselVar(key, event, "body", cardIndex)}
                onRemoveField={(key) => handleRemoveCarouselField(key, "body", cardIndex)}
                hasMissingSampleText={hasMissingSampleText}
                disabled={isDisabled}
            />
          )}
        </>
    );
};

export default CarouselTemplateCardBodyPreview;